/**
 * ScenarioPicker.tsx — 支付场景 / 通道场景选择卡片网格（共享原子）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 的 UI.ScenarioPicker（支付页面创建 +
 * 运营活动创建复用，杜绝各弹窗二次手写卡片）。规格逐属性对齐原型：
 *   - 2 列网格 gap 6；卡片 padding 10 / radius 6；选中 = 边框 var(--accent) + 底 var(--accent-soft)
 *   - 每卡三行：label 行（check + label + spacer + builtin chip「系统预置」）、mono value 行、desc 行
 *   - check 图标恒渲染、visibility 切换（稳定高度：选中切换不重排）
 * 接真扩展（原型无）：disabled 禁用态（cursor not-allowed + 降透明度 + 点击 no-op），
 * chipText 覆盖徽章文案（如 NewPaywallPageDialog 的「已存在」防重复创建）。
 */

import { Icons } from '~/lib/icons';

export interface ScenarioPickerItem<T extends string = string> {
  value: T;
  label: string;
  /** 场景一句话描述（第三行，缺省不渲染该行） */
  desc?: string;
  /** 内建场景 → label 行尾「系统预置」chip */
  builtin?: boolean;
  /** 禁用：不可选中、点击 no-op（如该 App 已存在同 scenario page） */
  disabled?: boolean;
  /** 覆盖徽章文案（默认 builtin 时「系统预置」）；disabled 非 builtin 场景用（如「已存在」） */
  chipText?: string;
}

export function ScenarioPicker<T extends string>({
  items,
  value,
  onSelect,
}: {
  items: ScenarioPickerItem<T>[];
  value: string;
  onSelect: (v: T) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
      {items.map((it) => {
        const on = !it.disabled && value === it.value;
        const chipText = it.chipText ?? (it.builtin ? '系统预置' : undefined);
        return (
          <button
            key={it.value}
            type="button"
            disabled={!!it.disabled}
            // 禁用原因悬浮提示：系统预置场景 vs (app_id, scenario) 唯一约束，帮运营理解为何不可选
            title={
              it.disabled
                ? chipText === '系统预置'
                  ? '系统预置场景 · App 创建时已预置，不可重复创建'
                  : '该 App 已存在此场景 ·（app_id, scenario）唯一'
                : undefined
            }
            onClick={() => {
              if (!it.disabled) onSelect(it.value);
            }}
            className="focusable"
            style={{
              padding: 10,
              borderRadius: 6,
              textAlign: 'left',
              border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: it.disabled
                ? 'var(--bg-subtle)'
                : on
                  ? 'var(--accent-soft)'
                  : 'var(--bg-elev)',
              cursor: it.disabled ? 'not-allowed' : 'pointer',
              opacity: it.disabled ? 0.6 : 1,
              font: 'inherit',
            }}
          >
            <div className="hstack" style={{ gap: 6, marginBottom: 2 }}>
              {/* check 恒渲染 + visibility 切换：选中/取消不引起 label 水平位移（稳定高度） */}
              <Icons.IconCheck
                size={11}
                style={{
                  color: 'var(--accent)',
                  flexShrink: 0,
                  visibility: on ? 'visible' : 'hidden',
                }}
              />
              <span
                style={{ fontSize: 12.5, fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {it.label}
              </span>
              <div style={{ flex: 1 }} />
              {chipText && (
                <span className="chip" style={{ fontSize: 9, padding: '0 5px', flexShrink: 0 }}>
                  {chipText}
                </span>
              )}
            </div>
            <div
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--fg-faint)',
                marginBottom: it.desc ? 2 : 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {it.value}
            </div>
            {it.desc && <div style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>{it.desc}</div>}
          </button>
        );
      })}
    </div>
  );
}
